/**
 * PatientInvoiceCard Component
 *
 * Displays a single invoice with its details and actions (View, Download PDF).
 */

import { Box, Chip, IconButton, Typography } from '@mui/material';
import {
  Receipt as ReceiptIcon,
  OpenInNew as OpenInNewIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';

import type { InvoiceResponse } from '@store/generated/payments';

/**
 * Format amount from cents to display string
 */
function formatAmount(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

/**
 * Format date string to display format
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Get status chip color based on invoice status
 */
function getStatusColor(
  status: string,
): 'success' | 'warning' | 'error' | 'default' | 'info' {
  switch (status.toLowerCase()) {
    case 'paid':
      return 'success';
    case 'open':
      return 'warning';
    case 'draft':
      return 'default';
    case 'uncollectible':
    case 'void':
      return 'error';
    default:
      return 'info';
  }
}

/**
 * Capitalize the first letter of a string
 */
function capitalizeStatus(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

export interface PatientInvoiceCardProps {
  invoice: InvoiceResponse;
  onView: (invoice: InvoiceResponse) => void;
  onDownload: (invoice: InvoiceResponse) => void;
}

/**
 * Individual invoice card displaying invoice info with View and Download actions
 */
export function PatientInvoiceCard({
  invoice,
  onView,
  onDownload,
}: PatientInvoiceCardProps) {
  const isPaid = invoice.status.toLowerCase() === 'paid';
  const displayAmount = isPaid ? invoice.amount_paid : invoice.amount_due;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 1.5,
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        '&:hover': {
          borderColor: 'action.hover',
        },
      }}
    >
      {/* Left: Icon and invoice info */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <ReceiptIcon sx={{ color: 'text.secondary', fontSize: 24 }} />

        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" fontWeight={500}>
              {invoice.number || `Invoice ${invoice.id.slice(-8)}`}
            </Typography>
            <Chip
              label={capitalizeStatus(invoice.status)}
              size="small"
              color={getStatusColor(invoice.status)}
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
            <Typography variant="caption" color="text.secondary">
              {formatDate(invoice.created)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              |
            </Typography>
            <Typography variant="caption" fontWeight={500}>
              {formatAmount(displayAmount)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Right: Actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {invoice.hosted_invoice_url && (
          <IconButton
            size="small"
            onClick={() => onView(invoice)}
            sx={{ color: 'primary.main' }}
            aria-label="View invoice"
          >
            <OpenInNewIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}

        {invoice.pdf && (
          <IconButton
            size="small"
            onClick={() => onDownload(invoice)}
            sx={{ color: 'text.secondary' }}
            aria-label="Download invoice PDF"
          >
            <DownloadIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}

export default PatientInvoiceCard;
